import { useState, useEffect, useRef, useCallback } from 'react';
import { Trophy, X, MapPin, Calendar, Gift, Users, Star } from 'lucide-react';
import { api } from '../lib/api';

// ============================================
// CONFIGURACIÓN DE TIEMPOS (en milisegundos)
// ============================================
const TIMING = {
  INITIAL_WAIT: 10 * 60 * 1000,      // 10 minutos antes del primer globo
  DISPLAY_DURATION: 10 * 1000,        // 10 segundos visible cada globo
  INTERVAL_BETWEEN: 2 * 60 * 1000,    // 2 minutos entre globos
  PAUSE_AFTER_CLOSE: 10 * 60 * 1000,  // 10 minutos de pausa si el usuario cierra
  FADE_DURATION: 500,                  // 500ms para animación fade
};

// Storage keys
const STORAGE_KEYS = {
  SESSION_START: 'tuprima_sp_session_start',
  DISMISSED_AT: 'tuprima_sp_dismissed_at',
  PRIZE_INDEX: 'tuprima_sp_prize_index',
};

// Prize type configuration
const PRIZE_CONFIG = {
  major: {
    icon: Trophy,
    label: 'Premio Mayor',
    bgColor: 'bg-gradient-to-r from-amber-500 to-yellow-500',
    emoji: '🏆'
  },
  inverted: {
    icon: Star,
    label: 'Premio Invertido',
    bgColor: 'bg-gradient-to-r from-purple-500 to-pink-500',
    emoji: '🔄'
  },
  top_buyer: {
    icon: Gift,
    label: 'Top Comprador',
    bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    emoji: '🎯'
  },
  triple: {
    icon: Star,
    label: 'Premio Triple',
    bgColor: 'bg-gradient-to-r from-orange-500 to-red-500',
    emoji: '🎲'
  },
  referral: {
    icon: Users,
    label: 'Top Referidor',
    bgColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
    emoji: '👥'
  }
};

// Month names
const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export function SocialProofToast() {
  // State
  const [prizes, setPrizes] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [currentPrize, setCurrentPrize] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isFading, setIsFading] = useState(false);
  
  // Refs for timer management
  const mountedRef = useRef(true);
  const timerIdRef = useRef(null);
  const hideTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const isShowingRef = useRef(false);

  // ============================================
  // STORAGE HELPERS
  // ============================================
  const getSessionStart = useCallback(() => {
    let stored = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    if (!stored) {
      stored = Date.now().toString();
      sessionStorage.setItem(STORAGE_KEYS.SESSION_START, stored);
    }
    return parseInt(stored, 10);
  }, []);

  const getDismissedAt = useCallback(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.DISMISSED_AT);
    return stored ? parseInt(stored, 10) : null;
  }, []);

  const setDismissedAt = useCallback((time) => {
    sessionStorage.setItem(STORAGE_KEYS.DISMISSED_AT, time.toString());
  }, []);

  const clearDismissed = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEYS.DISMISSED_AT);
  }, []);

  const getPrizeIndex = useCallback(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.PRIZE_INDEX);
    return stored ? parseInt(stored, 10) : 0;
  }, []);

  const setPrizeIndex = useCallback((index) => {
    sessionStorage.setItem(STORAGE_KEYS.PRIZE_INDEX, index.toString());
  }, []);

  // ============================================
  // CLEAR ALL TIMERS
  // ============================================
  const clearAllTimers = useCallback(() => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  // ============================================
  // FETCH PRIZES (only once on mount)
  // ============================================
  useEffect(() => {
    mountedRef.current = true;
    
    const fetchData = async () => {
      try {
        const data = await api.getSocialProofPrizes();
        if (mountedRef.current) {
          setPrizes(data.prizes || []);
          setEnabled(data.settings?.enabled || false);
        }
      } catch (error) {
        console.error('Error fetching social proof:', error);
      }
    };
    
    fetchData();
    
    return () => {
      mountedRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // ============================================
  // HIDE CURRENT PRIZE
  // ============================================
  const hidePrize = useCallback(() => {
    if (!mountedRef.current) return;
    
    setIsFading(false);
    setIsVisible(false);
    setCurrentPrize(null);
    isShowingRef.current = false;
  }, []);

  // ============================================
  // SHOW ONE PRIZE FOR 10 SECONDS
  // ============================================
  const showOnePrize = useCallback(() => {
    if (!mountedRef.current || prizes.length === 0 || isShowingRef.current) {
      return;
    }
    
    // Mark as showing to prevent overlaps
    isShowingRef.current = true;
    
    // Get next prize using stored index (cycle through)
    const currentIndex = getPrizeIndex();
    const prize = prizes[currentIndex % prizes.length];
    
    // Update index for next time
    setPrizeIndex((currentIndex + 1) % prizes.length);
    
    // Show the prize immediately
    setCurrentPrize(prize);
    setIsFading(false);
    setIsVisible(true);
    
    // Start fade out 500ms before hiding
    fadeTimerRef.current = setTimeout(() => {
      if (mountedRef.current) {
        setIsFading(true);
      }
    }, TIMING.DISPLAY_DURATION - TIMING.FADE_DURATION);
    
    // Hide after 10 seconds
    hideTimerRef.current = setTimeout(() => {
      hidePrize();
    }, TIMING.DISPLAY_DURATION);
    
  }, [prizes, getPrizeIndex, setPrizeIndex, hidePrize]);

  // ============================================
  // SCHEDULE NEXT PRIZE (every 2 minutes)
  // ============================================
  const scheduleNextPrize = useCallback(() => {
    // Clear any existing timer
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }
    
    timerIdRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      
      // Check if paused due to user dismiss
      const dismissedAt = getDismissedAt();
      if (dismissedAt) {
        const timeSinceDismiss = Date.now() - dismissedAt;
        if (timeSinceDismiss < TIMING.PAUSE_AFTER_CLOSE) {
          // Still paused, schedule again after remaining pause time
          const remaining = TIMING.PAUSE_AFTER_CLOSE - timeSinceDismiss;
          timerIdRef.current = setTimeout(() => {
            clearDismissed();
            showOnePrize();
            scheduleNextPrize();
          }, remaining);
          return;
        }
        // Pause time passed, clear and continue
        clearDismissed();
      }
      
      // Show prize only if not currently showing one
      if (!isShowingRef.current) {
        showOnePrize();
      }
      
      // Schedule next one
      scheduleNextPrize();
      
    }, TIMING.INTERVAL_BETWEEN);
  }, [showOnePrize, getDismissedAt, clearDismissed]);

  // ============================================
  // HANDLE CLOSE BUTTON
  // ============================================
  const handleClose = useCallback(() => {
    // Clear all display timers
    clearAllTimers();
    
    // Fade out immediately
    setIsFading(true);
    
    setTimeout(() => {
      hidePrize();
    }, TIMING.FADE_DURATION);
    
    // Record dismiss time - this will pause for 10 minutes
    setDismissedAt(Date.now());
    
    // Schedule resume after pause
    timerIdRef.current = setTimeout(() => {
      if (mountedRef.current) {
        clearDismissed();
        showOnePrize();
        scheduleNextPrize();
      }
    }, TIMING.PAUSE_AFTER_CLOSE);
    
  }, [clearAllTimers, hidePrize, setDismissedAt, clearDismissed, showOnePrize, scheduleNextPrize]);

  // ============================================
  // MAIN EFFECT - Start timer after 10 minutes
  // ============================================
  useEffect(() => {
    // Don't start if not enabled or no prizes
    if (!enabled || prizes.length === 0) return;
    
    const sessionStart = getSessionStart();
    const now = Date.now();
    const timeInSession = now - sessionStart;
    
    // Check if currently paused from dismiss
    const dismissedAt = getDismissedAt();
    if (dismissedAt) {
      const timeSinceDismiss = now - dismissedAt;
      if (timeSinceDismiss < TIMING.PAUSE_AFTER_CLOSE) {
        // Still paused, wait for remaining pause time
        const remainingPause = TIMING.PAUSE_AFTER_CLOSE - timeSinceDismiss;
        timerIdRef.current = setTimeout(() => {
          if (mountedRef.current) {
            clearDismissed();
            showOnePrize();
            scheduleNextPrize();
          }
        }, remainingPause);
        
        return () => clearAllTimers();
      }
      // Pause expired, clear it
      clearDismissed();
    }
    
    // Calculate wait time (10 minutes from session start)
    const waitTime = Math.max(0, TIMING.INITIAL_WAIT - timeInSession);
    
    // Schedule first prize after initial wait
    timerIdRef.current = setTimeout(() => {
      if (mountedRef.current) {
        showOnePrize();
        scheduleNextPrize();
      }
    }, waitTime);
    
    // Cleanup
    return () => clearAllTimers();
    
  }, [enabled, prizes.length, getSessionStart, getDismissedAt, clearDismissed, showOnePrize, scheduleNextPrize, clearAllTimers]);

  // ============================================
  // FORMAT HELPERS
  // ============================================
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (paidAt, daysAgo) => {
    try {
      const date = paidAt ? new Date(paidAt) : new Date();
      if (!paidAt && daysAgo) {
        date.setDate(date.getDate() - daysAgo);
      }
      return `${MONTH_NAMES[date.getMonth()]}, ${date.getFullYear()}`;
    } catch {
      return '';
    }
  };

  // ============================================
  // RENDER
  // ============================================
  if (!enabled || !isVisible || !currentPrize) {
    return null;
  }

  const config = PRIZE_CONFIG[currentPrize.prize_type] || PRIZE_CONFIG.major;
  const Icon = config.icon;

  return (
    <div
      className={`fixed bottom-20 left-3 z-50 transition-all duration-500 ease-out transform ${
        isFading 
          ? 'opacity-0 scale-95 translate-y-2' 
          : 'opacity-100 scale-100 translate-y-0'
      }`}
      style={{ maxWidth: '260px' }}
      data-testid="social-proof-toast"
    >
      <div className={`${config.bgColor} rounded-xl shadow-lg overflow-hidden`}>
        {/* Header */}
        <div className="px-3 py-1.5 bg-black/10 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-sm">{config.emoji}</span>
            <span className="text-xs font-medium text-white/90">{config.label}</span>
          </div>
          <button
            onClick={handleClose}
            className="text-white/60 hover:text-white transition-colors p-0.5 rounded-full hover:bg-white/20"
            aria-label="Cerrar"
            data-testid="social-proof-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-white truncate">
                {currentPrize.winner_name}
              </p>
              <div className="flex items-center gap-2 text-white/80">
                <div className="flex items-center gap-0.5 text-xs">
                  <MapPin className="w-2.5 h-2.5" />
                  <span className="truncate">{currentPrize.city}</span>
                </div>
                <span className="font-bold text-sm text-white">
                  {formatCurrency(currentPrize.amount)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 mt-1.5 text-white/60 text-xs justify-end">
            <Calendar className="w-2.5 h-2.5" />
            <span>{formatDate(currentPrize.paid_at, currentPrize.days_ago)}</span>
          </div>
        </div>
        
        {/* Progress bar - 10 seconds */}
        <div className="h-0.5 bg-black/10">
          <div 
            className="h-full bg-white/40"
            style={{
              width: '100%',
              animation: isFading ? 'none' : `shrink ${TIMING.DISPLAY_DURATION / 1000}s linear forwards`
            }}
          />
        </div>
      </div>
      
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
